import React, { ReactNode } from 'react';
import { Theme, withStyles, WithStyles } from '@material-ui/core';
import { useResizableSplitPane } from './useResizableSplitPane';
import {
  DEFAULT_INITIAL_LEFT_WIDTH,
  DEFAULT_MIN_RIGHT_WIDTH,
  RESIZE_HANDLE_WIDTH
} from './constants';
import { InitialLeftWidthInput } from './utils';

const styles = (_theme: Theme) => ({
  container: {
    display: 'flex',
    height: '100%',
    width: '100%'
  },
  leftPanel: {
    boxSizing: 'border-box' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    position: 'relative' as const,
    overflow: 'hidden' as const,
    minHeight: 0
  },
  resizeHandle: {
    width: RESIZE_HANDLE_WIDTH,
    flexShrink: 0,
    cursor: 'col-resize' as const
  },
  rightPanel: {
    boxSizing: 'border-box' as const,
    flex: '1 1 auto',
    overflow: 'hidden' as const,
    minHeight: 0,
    minWidth: 0
  }
});

type ResizableSplitLayoutProps = WithStyles<typeof styles> & {
  className?: string;
  leftPanel: ReactNode;
  rightPanel: ReactNode;
  leftPanelClassName?: string;
  rightPanelClassName?: string;
  initialLeftWidth?: InitialLeftWidthInput;
  minLeftWidth?: number;
  minRightWidth?: number;
  handleWidth?: number;
  onResize?: () => void;
  onResizeEnd?: () => void;
};

const ResizableSplitLayout = ({
  classes,
  className,
  leftPanel,
  rightPanel,
  leftPanelClassName,
  rightPanelClassName,
  initialLeftWidth = DEFAULT_INITIAL_LEFT_WIDTH,
  minLeftWidth,
  minRightWidth = DEFAULT_MIN_RIGHT_WIDTH,
  handleWidth = RESIZE_HANDLE_WIDTH,
  onResize,
  onResizeEnd
}: ResizableSplitLayoutProps) => {
  const {
    containerRef,
    leftPanelStyle,
    rightPanelStyle,
    leftWidthPx,
    maxLeftWidthPx,
    resolvedMinLeftWidth,
    handleMouseDown,
    handleKeyDown
  } = useResizableSplitPane({
    initialLeftWidth,
    minLeftWidth,
    minRightWidth,
    handleWidth,
    onResize,
    onResizeEnd
  });

  return (
    <div ref={containerRef} className={`${classes.container} ${className || ''}`}>
      <div className={`${classes.leftPanel} ${leftPanelClassName || ''}`} style={leftPanelStyle}>
        {leftPanel}
      </div>
      <div
        className={classes.resizeHandle}
        onMouseDown={handleMouseDown}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize panels"
        aria-valuemin={resolvedMinLeftWidth}
        aria-valuemax={maxLeftWidthPx ?? resolvedMinLeftWidth}
        aria-valuenow={leftWidthPx ?? resolvedMinLeftWidth}
      />
      <div
        className={`${classes.rightPanel} ${rightPanelClassName || ''}`}
        style={rightPanelStyle}>
        {rightPanel}
      </div>
    </div>
  );
};

export default withStyles(styles)(ResizableSplitLayout);
